import { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentSnapshot } from 'firebase/firestore';
import { messageService, MESSAGES_PAGE_SIZE } from '../api/messageService';
import { Conversation, Message } from '../types';

export const useChat = (conversation: Conversation | null) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Store the oldest document snapshot for pagination
  const oldestDocRef = useRef<DocumentSnapshot | null>(null);

  useEffect(() => {
    if (!conversation?.id) {
      setMessages([]);
      oldestDocRef.current = null;
      setHasMore(false);
      return;
    }

    // Clear messages from previous conversation immediately to avoid stale data
    setMessages([]);
    oldestDocRef.current = null;
    setHasMore(false);

    // Guest inquiries don't have a messages subcollection in the conversations collection
    // They are stored in guestInquiries collection with no messages sub-collection
    // Skip subscription for inquiry type to avoid permission denied errors
    if (conversation.metadata?.type === 'inquiry') {
      setLoading(false);
      return; // No subscription for guest inquiries
    }

    setLoading(true);

    // Subscribe to real-time updates for recent messages (paginated)
    const unsubscribe = messageService.subscribeToMessagesPaginated(
      conversation.id,
      MESSAGES_PAGE_SIZE,
      (newMessages, firstDoc, hasMoreMessages) => {
        setMessages(newMessages);
        oldestDocRef.current = firstDoc;
        setHasMore(hasMoreMessages);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [conversation?.id, conversation?.metadata?.type]);

  /**
   * Load older messages (for infinite scroll)
   */
  const loadMoreMessages = useCallback(async () => {
    if (!conversation?.id || !oldestDocRef.current || loadingMore || !hasMore) {
      return;
    }

    setLoadingMore(true);
    try {
      const result = await messageService.loadOlderMessages(
        conversation.id,
        oldestDocRef.current,
        MESSAGES_PAGE_SIZE
      );

      // Prepend older messages
      setMessages((prev) => [...result.messages, ...prev]);
      oldestDocRef.current = result.firstDoc;
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Failed to load older messages', error);
    } finally {
      setLoadingMore(false);
    }
  }, [conversation?.id, loadingMore, hasMore]);

  const sendMessage = useCallback(
    async (senderId: string, content: string) => {
      if (!conversation?.id || !content.trim()) return;
      setSending(true);
      try {
        await messageService.sendMessage(
          conversation.id,
          senderId,
          content,
          'text',
          undefined,
          conversation.participantIds
        );
      } catch (error) {
        console.error('Failed to send message', error);
        throw error;
      } finally {
        setSending(false);
      }
    },
    [conversation]
  );

  const sendAttachment = useCallback(
    async (senderId: string, file: File) => {
      if (!conversation?.id) return;
      setSending(true);
      try {
        await messageService.sendAttachment(conversation.id, senderId, file);
      } catch (error) {
        console.error('Failed to send attachment', error);
        throw error;
      } finally {
        setSending(false);
      }
    },
    [conversation]
  );

  return {
    messages,
    loading,
    loadingMore,
    sending,
    hasMore,
    sendMessage,
    sendAttachment,
    loadMoreMessages,
  };
};
